import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { search } from '../utils/api';
import { parseLocalDate } from '../utils/dates';

const debounce = (fn, ms) => {
  let timer;
  return (...args) => { clearTimeout(timer); timer = setTimeout(() => fn(...args), ms); };
};

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');
const fmtDate = (d) => {
  const date = parseLocalDate(d);
  return date ? date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '';
};

const ResultGroup = ({ title, items, renderItem }) => {
  if (!items.length) return null;
  return (
    <div className="search-group">
      <h3 className="search-group-title">{title}</h3>
      <ul className="search-results">
        {items.map(renderItem)}
      </ul>
    </div>
  );
};

const ResultLink = ({ to, label, meta }) => (
  <li>
    <Link to={to}>
      {label}
      {meta && <span className="search-result-meta">{meta}</span>}
    </Link>
  </li>
);

const Search = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const doSearch = useCallback(
    debounce((q) => {
      if (q.length < 2) { setResults(null); return; }
      setLoading(true);
      search(q).then(({ data }) => setResults(data)).finally(() => setLoading(false));
    }, 300),
    []
  );

  const handleChange = (e) => {
    setQuery(e.target.value);
    doSearch(e.target.value);
  };

  const total = results ? Object.values(results).reduce((s, arr) => s + arr.length, 0) : 0;

  return (
    <div className="page">
      <div className="page-header"><h1>Search</h1></div>
      <div className="search-bar-wrap">
        <input
          className="search-input"
          placeholder="Search tasks, projects, rooms, appliances, contacts…"
          value={query}
          onChange={handleChange}
          autoFocus
        />
      </div>

      {loading && <div className="loading">Searching…</div>}

      {results && !loading && (
        total === 0 ? (
          <p className="empty-state">No results for "{query}"</p>
        ) : (
          <div className="search-results-wrap">
            <ResultGroup title="Tasks" items={results.tasks} renderItem={(t) => (
              <ResultLink
                key={t._id}
                to="/tasks"
                label={t.title}
                meta={[cap(t.category), t.nextDue && `Due ${fmtDate(t.nextDue)}`].filter(Boolean).join(' · ')}
              />
            )} />
            <ResultGroup title="Projects" items={results.projects} renderItem={(p) => (
              <ResultLink
                key={p._id}
                to={`/projects/${p._id}`}
                label={p.title}
                meta={[cap(p.status?.replace('-', ' ')), p.priority && `${cap(p.priority)} priority`].filter(Boolean).join(' · ')}
              />
            )} />
            <ResultGroup title="Rooms" items={results.rooms} renderItem={(r) => (
              <ResultLink
                key={r._id}
                to={`/rooms/${r._id}/edit`}
                label={r.name}
                meta={r.floor ? cap(String(r.floor)) : ''}
              />
            )} />
            <ResultGroup title="Appliances" items={results.appliances} renderItem={(a) => (
              <ResultLink
                key={a._id}
                to={`/appliances/${a._id}`}
                label={a.name}
                meta={[cap(a.category), a.make].filter(Boolean).join(' · ')}
              />
            )} />
            <ResultGroup title="Contacts" items={results.contacts} renderItem={(c) => (
              <ResultLink
                key={c._id}
                to={`/contacts/${c._id}/edit`}
                label={c.name}
                meta={[cap(c.trade), c.company].filter(Boolean).join(' · ')}
              />
            )} />
          </div>
        )
      )}
    </div>
  );
};

export default Search;
